import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiError } from "@/core/api/types";

/** The params that say which period is on screen; every tab keeps them. */
const PERIOD_PARAMS = ["period", "compare"] as const;

/**
 * The period on screen and what it is compared with, kept in the URL so a
 * link opens the same view and every tab shows the same period:
 * `?period=<run id>` (latest when absent) and
 * `?compare=average_4|last_year|run:<id>` (the period before when absent).
 */
export const usePeriodParams = () => {
  const [params, setParams] = useSearchParams();
  const setCompare = (next: string | null) =>
    setParams((current) => {
      const updated = new URLSearchParams(current);
      if (next) updated.set("compare", next);
      else updated.delete("compare");
      return updated;
    });
  // Another period keeps a preset comparison ("4-week average") but not a
  // week picked against the last one, which may be the new period itself.
  // Its workbook has other sheets, so the open sheet is forgotten too.
  const setPeriod = (next: string | null) =>
    setParams((current) => {
      const updated = new URLSearchParams(current);
      if (next) updated.set("period", next);
      else updated.delete("period");
      if (updated.get("compare")?.startsWith("run:")) updated.delete("compare");
      updated.delete("sheet");
      return updated;
    });
  const search = () => {
    const kept = new URLSearchParams();
    for (const key of PERIOD_PARAMS) {
      const value = params.get(key);
      if (value) kept.set(key, value);
    }
    const text = kept.toString();
    return text ? `?${text}` : "";
  };
  return {
    periodId: params.get("period"),
    compare: params.get("compare"),
    setCompare,
    setPeriod,
    search,
  };
};

/**
 * The period or comparison the URL names may have been removed since it was
 * opened (from All periods, or by the agent chat): the page then shows the
 * latest period, or compares with the period before, instead of an error.
 */
export const useForgetRemovedPeriod = (
  error: unknown,
  params: ReturnType<typeof usePeriodParams>,
) => {
  const status = error instanceof ApiError ? error.status : null;
  const { periodId, compare, setPeriod, setCompare } = params;
  useEffect(() => {
    if (status === 404 && periodId) setPeriod(null);
    else if (status === 400 && compare?.startsWith("run:")) setCompare(null);
  }, [status, periodId, compare, setPeriod, setCompare]);
};
